/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * useFeedData
 * -----------
 * React Query hooks for the athlete feed. Each wraps one feedApi call; the
 * model work (normalising, weekly totals) happens in useMemo over the cached
 * rows so a re-render never re-fetches.
 */

import { useEffect, useMemo, useRef } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { retrieve } from '../../utils/storage_helper';
import {
  ACTIVITY_PAGE_SIZE, fetchActivityPage, fetchActivity,
  fetchRoutePoints, fetchSplits, fetchSuggestedEvents, fetchAthleteSummary, viewerTimeZone,
  fetchActivitiesInRange,
  fetchActivityChanges, EVENT_TABLE,
} from './feedApi';
import { fetchPublicTableList } from '../../hooks/usePublicTableView';
import { fetchUserProfile } from './profile/profileApi';
import { normalizeActivities, normalizeActivity, routeFromPoints, hasRoute } from './feedModel';
import { readRoutes, writeRoute } from './routeCache';
import { loadGeoid } from './geoid';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** The signed-in application user, as the login response stored it. */
export function useFeedUser() {
  return feedUserFrom(retrieve('userData'));
}

/** Name, initials and handle from a stored `userData` (null → the placeholder athlete). */
export function feedUserFrom(stored) {
  const userData = stored || {};
  const first = userData.first_name || '';
  const last = userData.last_name || '';
  const name = `${first} ${last}`.trim() || userData.user_name || 'Athlete';
  const initials = (first && last ? first[0] + last[0] : name.slice(0, 2)).toUpperCase();
  return { userId: userData.user_id ?? null, name, firstName: first || name.split(' ')[0], initials, handle: userData.user_name || '' };
}

/** The activity list, newest first, one page of 20 at a time. */
export function useActivityFeed(userId) {
  const query = useInfiniteQuery({
    queryKey: ['feed', 'activities', userId],
    queryFn: ({ pageParam, signal }) => fetchActivityPage(apiHelper, { userId, page: pageParam, signal }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.rows.length, 0);
      return last.rows.length === ACTIVITY_PAGE_SIZE && loaded < last.count ? pages.length + 1 : undefined;
    },
    enabled: userId !== null,
    staleTime: 60 * 1000,
  });
  const activities = useMemo(
    () => normalizeActivities((query.data?.pages || []).flatMap((p) => p.rows)),
    [query.data],
  );
  return {
    activities,
    total: query.data?.pages?.[0]?.count ?? 0,
    isLoading: userId !== null && query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

const SYNC_POLL_MS = 60 * 1000;

/**
 * Checks every 60 s (only while the tab is visible, and on return to it)
 * whether the athlete's activities changed — a new UpVibe sync. When they
 * did, the list and the training summary refetch; no reload needed.
 */
export function useActivitySync(userId, enabled = true) {
  const queryClient = useQueryClient();
  const seen = useRef(null);
  const query = useQuery({
    queryKey: ['feed', 'changes', userId],
    queryFn: ({ signal }) => fetchActivityChanges(apiHelper, { signal }),
    enabled: userId !== null && enabled,
    refetchInterval: SYNC_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    if (seen.current !== null && seen.current !== data.fingerprint) {
      queryClient.invalidateQueries({ queryKey: ['feed', 'activities', userId] });
      queryClient.invalidateQueries({ queryKey: ['feed', 'summary', userId] });
    }
    seen.current = data.fingerprint;
  }, [query.data, queryClient, userId]);
}

/**
 * Registrations badge, "Next up", and the whole "Your training" card: five
 * saved analytics queries in one run-batch request. The sidebar badge and the
 * page share this one cache entry, so it is one request per page, not two.
 */
export function useAthleteSummary(userId) {
  const tz = viewerTimeZone();
  return useQuery({
    queryKey: ['feed', 'summary', userId, tz],
    queryFn: ({ signal }) => fetchAthleteSummary(apiHelper, { tz, signal }),
    enabled: userId !== null,
    staleTime: 60 * 1000,
  });
}

/** One activity for the detail panel — from the list when it is there, else fetched. */
export function useActivity(userId, activityId, fromList) {
  const query = useQuery({
    queryKey: ['feed', 'activity', userId, String(activityId)],
    queryFn: async ({ signal }) => normalizeActivity(await fetchActivity(apiHelper, { userId, activityId, signal })),
    enabled: userId !== null && !!activityId && !fromList,
    staleTime: 5 * 60 * 1000,
  });
  return { activity: fromList || query.data || null, isLoading: !fromList && query.isLoading, isError: query.isError };
}

/**
 * Routes seeded from the device store this session, and routes already written
 * to it. Module-level because the same route is read by a card and by the
 * detail panel, and neither should write it twice. Cleared on sign-out.
 */
const hydratedRoutes = new Set();
const writtenRoutes = new Set();

/** Forgets what was hydrated/written — called when the session ends. */
export function resetRouteCacheMemory() {
  hydratedRoutes.clear();
  writtenRoutes.clear();
}

/** Cache key of one activity's route points — shared by the feed cards and the drawer. */
export function routeQueryKey(activityId) {
  return ['feed', 'route', String(activityId)];
}

/**
 * Has this activity's route already been fetched this session? A card whose
 * route is in the cache draws it straight away instead of waiting out the
 * dwell delay again — and costs no request, since React Query serves it.
 */
export function useRouteAlreadyLoaded(activityId) {
  const queryClient = useQueryClient();
  return !!activityId && queryClient.getQueryData(routeQueryKey(activityId)) !== undefined;
}

/**
 * Seeds React Query with routes already stored on this device, for the
 * activities currently in the list. A card whose route is seeded draws it
 * straight away and never enters the dwell gate — so a refresh, or opening
 * the feed again tomorrow, costs no route requests at all.
 *
 * Runs once per activity per session; the store is read in one batch.
 */
export function useRouteHydration(userId, activities) {
  const queryClient = useQueryClient();
  const asked = useRef(new Set());

  useEffect(() => {
    if (userId === null || userId === undefined || !activities || !activities.length) return undefined;
    const ids = activities
      .filter((a) => hasRoute(a))
      .map((a) => String(a.id))
      .filter((id) => !asked.current.has(id)
        && queryClient.getQueryData(routeQueryKey(id)) === undefined);
    if (!ids.length) return undefined;
    ids.forEach((id) => asked.current.add(id));

    let cancelled = false;
    readRoutes(userId, ids).catch(() => new Map()).then((found) => {
      if (cancelled || !found || !found.size) return;
      found.forEach((rows, id) => {
        // Mark before seeding: seeding makes the query succeed, and that must
        // not be mistaken for a fresh download and written straight back.
        hydratedRoutes.add(id);
        queryClient.setQueryData(routeQueryKey(id), rows);
      });
    });
    return () => { cancelled = true; };
  }, [userId, activities, queryClient]);
}

/**
 * The EGM96 geoid grid for sea-level altitude (see geoid.js), fetched once per
 * session and only when asked for. `geoid` is null until loaded, and stays
 * null if it cannot be had — altitudes then show uncorrected, as in the app.
 */
export function useGeoid(enabled = true) {
  const query = useQuery({
    queryKey: ['egm96-geoid'],
    queryFn: () => loadGeoid(),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  return { geoid: query.data || null, isLoading: enabled && query.isPending };
}

export function useRoute(activityId, enabled = true) {
  const query = useQuery({
    queryKey: routeQueryKey(activityId),
    queryFn: ({ signal }) => fetchRoutePoints(apiHelper, { activityId, signal }),
    enabled: !!activityId && enabled,
    staleTime: 30 * 60 * 1000,
  });
  const route = useMemo(() => routeFromPoints(query.data), [query.data]);

  // Keep a freshly downloaded route on the device. Only downloads are stored:
  // a route that was seeded from the store is already there, and an empty
  // result is not stored at all (see routeCache).
  useEffect(() => {
    if (!activityId || !query.isSuccess || !route.length) return;
    const id = String(activityId);
    if (hydratedRoutes.has(id) || writtenRoutes.has(id)) return;
    writtenRoutes.add(id);
    const { userId } = feedUserFrom(retrieve('userData'));
    if (userId === null || userId === undefined) return;
    writeRoute(userId, id, route);
  }, [activityId, query.isSuccess, route]);

  return {
    route,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
    // The request is over, whichever way it went: the feed's download queue
    // uses this to let the next card through.
    settled: query.isSuccess || query.isError,
  };
}

export function useSplits(activityId, enabled = true) {
  return useQuery({
    queryKey: ['feed', 'splits', String(activityId)],
    queryFn: ({ signal }) => fetchSplits(apiHelper, { activityId, signal }),
    enabled: !!activityId && enabled,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Activities in one analytics range, normalized. Keyed by the range bounds, so
 * switching the type filter re-aggregates the cached rows without a request;
 * the previous range stays on screen (faded) while a new one loads, as the app
 * keeps its chart under the spinner.
 */
export function useAnalyticsActivities(userId, range) {
  const query = useQuery({
    queryKey: ['feed', 'analytics', userId, range.fromIso, range.toIso],
    queryFn: ({ signal }) => fetchActivitiesInRange(apiHelper, { userId, fromIso: range.fromIso, toIso: range.toIso, signal }),
    enabled: userId !== null,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const activities = useMemo(() => normalizeActivities(query.data), [query.data]);
  return {
    activities,
    hasData: !!query.data,
    isLoading: userId !== null && query.isFetching && (query.isPlaceholderData || !query.data),
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * The athlete's user_profile row — only preferred_unit is read here. Same key
 * and request as the profile page, so a unit change saved there shows here.
 */
export function useUserProfileRow(userId) {
  return useQuery({
    queryKey: ['profile', 'row', userId],
    queryFn: ({ signal }) => fetchUserProfile(apiHelper, { userId, signal }),
    enabled: userId !== null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSuggestedEvents(enabled = true) {
  return useQuery({
    queryKey: ['feed', 'suggested'],
    queryFn: () => fetchSuggestedEvents((params) => fetchPublicTableList(EVENT_TABLE, params), { limit: 6 }),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}
